import React from "react";

const CriteriaAndRules = () => {
  // Ensure all arrays are defined with empty arrays as fallback
  const membershipCriteria =
    [
      {
        category: "Eligibility Requirements",
        icon: "✅",
        items: [
          "Minimum age: 18 years on date of application",
          "No criminal history or involvement in illegal activities",
          "Ability to maintain good public relationships",
          "Understanding of voluntary nature (no monetary benefits)",
        ],
      },
      {
        category: "Member Responsibilities",
        icon: "📋",
        items: [
          "Explain association objectives to new members clearly",
          "Provide local event information and articles",
          "Identify local problems through community engagement",
          "Assist public in filing FIR during incidents",
        ],
      },
      {
        category: "Prohibited Activities",
        icon: "🚫",
        items: [
          "No illegal activities or criminal behavior",
          "No false promises about jobs or monetary benefits",
          "No disturbance of law and order",
          "No unauthorized fund collection",
        ],
      },
    ] || [];

  const idCardRules =
    [
      {
        rule: "PRESS/MEDIA Card Usage",
        description:
          "Issued card should not be misused under any circumstances",
        consequences: [
          "Immediate forfeiture without notice",
          "Legal action initiation",
          "Membership termination",
        ],
        icon: "🪪",
      },
      {
        rule: "Card Renewal Policy",
        description: "Renewal must be completed within 15 days of expiry",
        consequences: [
          "Return unused cards within 7 days",
          "Non-compliance leads to cancellation",
          "Legal action for non-return",
        ],
        icon: "🔄",
      },
      {
        rule: "Misuse Liability",
        description: "Card holder liable for any misuse under law and order",
        consequences: [
          "Personal legal responsibility",
          "Association not liable for misuse",
          "Criminal proceedings possible",
        ],
        icon: "⚖️",
      },
    ] || [];

  const financialRules =
    [
      {
        aspect: "Membership Fees",
        details: [
          "Fees used for association welfare and administration",
          "Non-refundable under any circumstances",
          "Part or full payments not refundable",
          "Used for society development work",
        ],
        note: "Contact 72000 24451/52/53 for fee details",
      },
      {
        aspect: "Receipt Book Accountability",
        details: [
          "Member fully accountable for receipt books",
          "Legal action for non-return",
          "Minimum penalty: ₹2,50,000 per book",
          "Strict documentation required",
        ],
        note: "Immediate return upon membership termination",
      },
      {
        aspect: "Fund Collection",
        details: [
          "No unauthorized fund or donation collection",
          "Written permission required from association",
          "Strict approval process for fundraising",
          "Transparent accounting mandatory",
        ],
        note: "Committee approval required for all collections",
      },
    ] || [];

  const applicationProcess =
    [
      {
        step: "1",
        title: "Document Submission",
        requirements: [
          "Completed application form",
          "Valid ID proof",
          "Address proof",
          "Recent photograph",
        ],
        timeline: "Within 30 days of payment",
      },
      {
        step: "2",
        title: "Payment Processing",
        requirements: [
          "Membership/Leadership fee payment",
          "No refunds for incomplete applications",
          "Cancellation after 30 days if documents not submitted",
          "Part payments also non-refundable",
        ],
        timeline: "Immediate upon receipt",
      },
      {
        step: "3",
        title: "Approval & Card Issuance",
        requirements: [
          "Committee review and approval",
          "PRESS/MEDIA card issuance",
          "Rules and regulations acknowledgment",
          "Membership activation",
        ],
        timeline: "Within 15 working days",
      },
    ] || [];

  const committeeGuidelines =
    [
      {
        guideline: "No Unauthorized Commitments",
        description:
          "Existing members cannot make promises without association consent",
        examples: [
          "No job opportunity guarantees",
          "No cash benefit promises",
          "No assurance of personal request fulfillment",
        ],
      },
      {
        guideline: "Assistance Protocol",
        description:
          "Personal problems and job opportunities require committee approval",
        process: [
          "High-level committee consultation",
          "Case-by-case evaluation",
          "Assistance without guarantees",
          "Transparent decision making",
        ],
      },
      {
        guideline: "Legal Compliance",
        description: "Strict adherence to all laws and regulations",
        requirements: [
          "Respect traffic rules and regulations",
          "Maintain public order",
          "Follow all legal procedures",
          "Jurisdiction: Chennai only for disputes",
        ],
      },
    ] || [];

  // Safe mapping functions with error handling
  const safeMap = (array, renderFunction) => {
    if (!array || !Array.isArray(array)) {
      return (
        <div className="text-center text-gray-500 py-8">No data available</div>
      );
    }
    return array.map(renderFunction);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-orange-900 to-orange-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Criteria & Rules and Regulations
            </h1>
            <p className="text-xl md:text-2xl text-orange-100 max-w-4xl mx-auto">
              Comprehensive Guidelines for NAI Membership and Conduct
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Membership Guidelines
            </h2>
            <div className="w-24 h-1 bg-orange-600 mx-auto rounded-full"></div>
          </div>

          <div className="prose prose-lg max-w-none text-gray-700 text-center">
            <p className="text-lg leading-relaxed mb-6">
              NAI maintains strict criteria and regulations to ensure
              professional conduct, legal compliance, and ethical journalism
              practices among all members.
            </p>
            <p className="text-sm text-gray-500">
              Complete association details available at: www.naiindia.in
            </p>
          </div>
        </div>
      </section>

      {/* Membership Criteria */}
      <section className="py-20 bg-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Membership Criteria
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Essential requirements and responsibilities for NAI members
            </p>
            <div className="w-24 h-1 bg-orange-600 mx-auto mt-6 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {safeMap(membershipCriteria, (category, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 overflow-hidden border border-gray-100"
              >
                <div className="bg-gradient-to-r from-orange-600 to-orange-700 p-6">
                  <div className="flex items-center justify-center mb-4">
                    <div className="text-3xl">{category.icon}</div>
                  </div>
                  <h3 className="text-xl font-bold text-white text-center">
                    {category.category}
                  </h3>
                </div>

                <div className="p-6">
                  <ul className="space-y-3">
                    {safeMap(category.items, (item, itemIndex) => (
                      <li
                        key={itemIndex}
                        className="flex items-start text-gray-700"
                      >
                        <svg
                          className="w-5 h-5 text-orange-500 mr-3 mt-0.5 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ID Card Regulations */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Identity Card Regulations
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Strict guidelines for PRESS/MEDIA card usage and maintenance
            </p>
            <div className="w-24 h-1 bg-orange-600 mx-auto mt-6 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {safeMap(idCardRules, (rule, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-6 border border-orange-200 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="text-center mb-6">
                  <div className="text-3xl mb-4">{rule.icon}</div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {rule.rule}
                  </h3>
                </div>

                <p className="text-gray-700 leading-relaxed mb-4 text-center">
                  {rule.description}
                </p>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wide">
                    Consequences:
                  </h4>
                  <ul className="space-y-2">
                    {safeMap(rule.consequences, (consequence, cIndex) => (
                      <li
                        key={cIndex}
                        className="flex items-start text-sm text-gray-600"
                      >
                        <span className="text-red-500 mr-2 mt-1">•</span>
                        {consequence}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Financial Regulations */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Financial Regulations
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Strict financial accountability and fee management policies
            </p>
            <div className="w-24 h-1 bg-orange-600 mx-auto mt-6 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {safeMap(financialRules, (rule, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 hover:shadow-lg transition-shadow duration-300 border border-gray-200"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                  {rule.aspect}
                </h3>

                <ul className="space-y-3 mb-4">
                  {safeMap(rule.details, (detail, detailIndex) => (
                    <li
                      key={detailIndex}
                      className="flex items-start text-sm text-gray-600"
                    >
                      <span className="text-orange-500 mr-2 mt-1">▸</span>
                      {detail}
                    </li>
                  ))}
                </ul>

                <div className="border-t border-gray-200 pt-4">
                  <p className="text-xs text-orange-600 font-semibold text-center">
                    {rule.note}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Application Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Step-by-step procedure for membership application and approval
            </p>
            <div className="w-24 h-1 bg-orange-600 mx-auto mt-6 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {safeMap(applicationProcess, (step, index) => (
              <div
                key={index}
                className="bg-orange-50 rounded-2xl p-6 border-2 border-orange-200"
              >
                <div className="text-center mb-6">
                  <span className="inline-block bg-orange-600 text-white w-8 h-8 rounded-full text-sm font-bold flex items-center justify-center mx-auto">
                    {step.step}
                  </span>
                  <h3 className="text-xl font-bold text-gray-900 mt-4">
                    {step.title}
                  </h3>
                  <div className="text-xs text-orange-600 font-semibold mt-2">
                    Timeline: {step.timeline}
                  </div>
                </div>

                <ul className="space-y-2">
                  {safeMap(step.requirements, (requirement, reqIndex) => (
                    <li
                      key={reqIndex}
                      className="flex items-start text-sm text-gray-600"
                    >
                      <span className="text-orange-500 mr-2 mt-0.5">•</span>
                      {requirement}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Committee Guidelines */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-orange-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white bg-opacity-10 rounded-2xl p-8 backdrop-blur-sm">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-4">Committee Guidelines</h2>
              <p className="text-orange-100 text-lg">
                High-level committee protocols and member conduct expectations
              </p>
              <div className="w-24 h-1 bg-white mx-auto mt-4 rounded-full"></div>
            </div>

            <div className="space-y-8">
              {safeMap(committeeGuidelines, (guideline, index) => (
                <div
                  key={index}
                  className="bg-white bg-opacity-20 rounded-xl p-6"
                >
                  <h3 className="text-xl font-bold mb-3">
                    {guideline.guideline}
                  </h3>
                  <p className="text-orange-100 mb-4">
                    {guideline.description}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold text-sm uppercase tracking-wide mb-2">
                        {guideline.examples ? "Examples:" : "Process:"}
                      </h4>
                      <ul className="space-y-1 text-orange-100 text-sm">
                        {safeMap(
                          guideline.examples || guideline.process,
                          (item, itemIndex) => (
                            <li key={itemIndex} className="flex items-start">
                              <span className="text-green-300 mr-2">•</span>
                              {item}
                            </li>
                          )
                        )}
                      </ul>
                    </div>

                    {guideline.requirements && (
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wide mb-2">
                          Requirements:
                        </h4>
                        <ul className="space-y-1 text-orange-100 text-sm">
                          {safeMap(guideline.requirements, (req, reqIndex) => (
                            <li key={reqIndex} className="flex items-start">
                              <span className="text-yellow-300 mr-2">•</span>
                              {req}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Legal Disclaimer */}
      <section className="py-20 bg-red-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Legal Disclaimer
            </h2>
            <div className="w-24 h-1 bg-red-600 mx-auto rounded-full"></div>
          </div>

          <div className="bg-white rounded-2xl p-8 border border-red-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="text-2xl text-red-600 mr-3">⚖️</span>
                  Jurisdiction & Disputes
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    All disputes addressed in Chennai jurisdiction only
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    Legal action for rule violations
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    Minimum penalty: ₹2,50,000 for receipt book non-return
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="text-2xl text-red-600 mr-3">📞</span>
                  Contact Information
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-center">
                    <span className="text-red-500 mr-2">•</span>
                    Membership Fees: 72000 24451 / 52 / 53
                  </li>
                  <li className="flex items-center">
                    <span className="text-red-500 mr-2">•</span>
                    Emergency: NAI Helpline
                  </li>
                  <li className="flex items-center">
                    <span className="text-red-500 mr-2">•</span>
                    Email: info@naiindia.in
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Understand Our Rules & Regulations
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto text-gray-300">
            Ensure compliance with all NAI guidelines and maintain the highest
            standards of journalistic conduct
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <button className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-300">
              Download Complete Rules
            </button>
            <button className="border-2 border-white hover:bg-white hover:text-gray-900 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-300">
              Contact Membership Committee
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CriteriaAndRules;
